import {
  clearFlatTireSimulation,
  markFlatTireWorkOrderSeen,
  useAdFleetMapVehicles,
  useFlatTireSimulationCreatedAt,
  useWasFlatTireWorkOrderSeen,
} from '@backoffice-frontend/ad-fleet-map'
import {
  getOnBoardPassengers,
  getPassengerCount,
} from '@backoffice-frontend/ad-passenger-list'
import { useTogglePoolingMutation } from '@backoffice-frontend/ad-pooling-toggle'
import { useInFieldSupportWorkOrder } from '@backoffice-frontend/ad-vehicle-stream'
import {
  getBackgroundColor as getAlertBackgroundColor,
  getAlertIconComponent,
  getDisabledForegroundColor,
} from '@backoffice-frontend/alerts-common'
import { useErrorHandlingMutation } from '@backoffice-frontend/data-fetching'
import {
  FccAdAlertType,
  WorkOrderServiceItem,
} from '@backoffice-frontend/graphql'
import { useCountUpSince } from '@backoffice-frontend/hooks'
import { Divider } from '@backoffice-frontend/patterns'
import type { Theme } from '@emotion/react'
import { PersonIcon, TrafficConeIcon } from '@moia-dev/pace-icons'
import { Icon, Text } from '@moia-dev/pace-web'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

const FLAT_TIRE_ALERT = {
  __typename: 'FccAdAlert' as const,
  type: FccAdAlertType.EmergencyStop,
}

type FlatTireAlertFeedCardProps = {
  vehicleId: string
}

export const FlatTireAlertFeedCard = ({
  vehicleId,
}: FlatTireAlertFeedCardProps) => {
  const { t } = useTranslation()
  const { vehicles } = useAdFleetMapVehicles()
  const vehicle = vehicles.find(v => v.id === vehicleId)
  const {
    activeWorkOrder,
    status: ifuStatus,
    isLoading,
  } = useInFieldSupportWorkOrder(
    vehicleId,
    WorkOrderServiceItem.OnSiteTireChange,
  )
  const [togglePooling] = useErrorHandlingMutation(useTogglePoolingMutation)
  const createdAt = useFlatTireSimulationCreatedAt(vehicleId)
  const workOrderSeen = useWasFlatTireWorkOrderSeen(vehicleId)

  useEffect(() => {
    if (activeWorkOrder) {
      markFlatTireWorkOrderSeen(vehicleId)
    }
  }, [activeWorkOrder, vehicleId])

  useEffect(() => {
    if (!isLoading && createdAt && workOrderSeen && !activeWorkOrder) {
      void togglePooling({
        variables: { vehicleId, poolingEnabled: true },
      })
      clearFlatTireSimulation(vehicleId)
    }
  }, [
    isLoading,
    createdAt,
    workOrderSeen,
    activeWorkOrder,
    togglePooling,
    vehicleId,
  ])

  const timestamp = useCountUpSince(
    createdAt ? { since: createdAt } : { since: new Date() },
  )

  if (!createdAt || !vehicle) {
    return null
  }

  const passengerCount = getPassengerCount(
    getOnBoardPassengers(vehicle.fccUpcomingWaypoints ?? []),
  )

  const IconComponent = getAlertIconComponent('Highest')

  return (
    <div
      css={theme => ({
        border: `1px solid ${theme.semantic.ColorAccessoryReduced}`,
        borderTopWidth: '8px',
        backgroundColor: theme.semantic.ColorSurfaceDefault,
        padding: theme.space.Space2,
        borderRadius: theme.radius.RadiusMedium,
        overflow: 'hidden',
      })}
    >
      {/* Header */}
      <div
        css={theme => ({
          display: 'flex',
          alignItems: 'center',
          gap: theme.space.Space2,
          paddingBottom: theme.space.Space2,
        })}
      >
        <Icon
          size={24}
          css={theme => ({
            backgroundColor: getAlertBackgroundColor(FLAT_TIRE_ALERT, theme),
            borderRadius: 4,
          })}
        >
          <IconComponent
            css={(theme: Theme) => ({
              color: getDisabledForegroundColor(FLAT_TIRE_ALERT, theme),
            })}
          />
        </Icon>
        <Text
          size="lg"
          css={theme => ({
            fontWeight: 500,
            color: theme.semantic.ColorContentDisabled,
          })}
        >
          {vehicle.label}
        </Text>
        <div
          css={theme => ({
            display: 'flex',
            alignItems: 'center',
            gap: theme.space.Space1,
            backgroundColor: theme.semantic.ColorAccessoryReducedAlternative,
            borderRadius: 9999,
            padding: `${theme.space.Space05} ${theme.space.Space2}`,
          })}
        >
          <PersonIcon
            css={theme => ({
              width: 16,
              height: 16,
              color: theme.semantic.ColorContentDisabled,
            })}
          />
          <Text
            size="md"
            css={theme => ({ color: theme.semantic.ColorContentDisabled })}
          >
            {passengerCount}
          </Text>
        </div>
      </div>

      <Divider />

      {/* Alert row */}
      <div
        css={theme => ({
          display: 'flex',
          flexDirection: 'column',
          paddingTop: theme.space.Space2,
        })}
      >
        <div
          css={theme => ({
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: theme.space.Space1,
          })}
        >
          <Text
            size="md"
            weight="emphasized"
            css={theme => ({ color: theme.semantic.ColorContentDisabled })}
          >
            {t('adFleetMap_flatTireSimulation_alertTitle')}
          </Text>
          {timestamp && (
            <Text
              size="sm"
              weight="emphasized"
              css={theme => ({
                color: theme.semantic.ColorContentDisabled,
                minWidth: '4ch',
              })}
            >
              {timestamp}
            </Text>
          )}
        </div>
      </div>

      {/* Footer — IFU status */}
      <div
        css={theme => ({
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          margin: `${theme.space.Space2} -${theme.space.Space2} -${theme.space.Space2}`,
          padding: theme.space.Space2,
          gap: theme.space.Space2,
          borderTop: `1px solid ${theme.semantic.ColorAccessoryReduced}`,
          backgroundColor: theme.semantic.ColorSurfaceReducedAlternative,
        })}
      >
        <div
          css={theme => ({
            display: 'flex',
            alignItems: 'center',
            gap: theme.space.Space2,
          })}
        >
          <Icon size={16}>
            <TrafficConeIcon
              css={theme => ({ color: theme.semantic.ColorContentDisabled })}
            />
          </Icon>
          <Text
            size="sm"
            css={theme => ({ color: theme.semantic.ColorContentDisabled })}
          >
            {ifuStatus === 'dispatched'
              ? t('adFleetMap_flatTireSimulation_unitDispatched')
              : t('adFleetMap_flatTireSimulation_unitRequested')}
          </Text>
        </div>
        {activeWorkOrder?.orderNumber && (
          <Text
            size="sm"
            css={theme => ({ color: theme.semantic.ColorContentDisabled })}
          >
            #{activeWorkOrder.orderNumber}
          </Text>
        )}
      </div>
    </div>
  )
}
